import {
	GridStack as Grid,
	GridItemHTMLElement,
	GridStackNode,
	GridStackOptions,
	GridStackWidget,
	Utils,
} from 'gridstack'

import { constant } from './constant'
import {
	getGridStackCompact,
	getGridStackCompactHandler,
	getGridStackFloatHandler,
	getGridStackNewBlockSelector,
	getGridStackTargetSelector,
} from './selectors'

import { AbstractResizer } from '@/abstractModule'
import { modifier } from '@/constant'
import { useEvent, useState } from '@/hooks'
import { blockBuilderSelector, resizerSelectors } from '@/selectors'
import { getChartSelectors } from '@/selectors/chart'
import { cn, debounce } from '@/utils'
import { addClass, removeClass } from '@/utils/class'

const { selector } = constant

const options: GridStackOptions & GridStackWidget = {
	column: 24,
	disableResize: true,
	disableDrag: true,
	alwaysShowResizeHandle: true,
	float: true,
	acceptWidgets: selector.newBlock,
	margin: 5,
	cellHeight: 40,
	minRow: 1,
	resizable: {
		handles: 'all',
	},
}

export type GridStackCustomEvent = {
	detail?: Array<GridStackWidget>
} & Event

type InitProps = {
	enableResizing?: boolean
	onInit?: () => void
	onAfterInit?: () => void
	onAdd?: (e: GridStackCustomEvent) => void
	onResizeStart?: (event: Event, el: GridItemHTMLElement) => void
	onResizeComplete?: (event: Event, el: GridItemHTMLElement) => void
	onDragComplete?: (event: Event, el: GridItemHTMLElement) => void
	onWindowResize?: () => void
}

type State = {
	gridStacks: Grid[]
	active: boolean
	initialized: boolean
	blockBuilder: HTMLElement | null
} & InitProps

export const GridStack = (() => {
	const autoHeightObservers = new Map<GridItemHTMLElement, ResizeObserver>()

	const cleanupAutoHeightObservers = () => {
		for (const [item, observer] of autoHeightObservers) {
			if (!document.body.contains(item)) {
				observer.disconnect()
				autoHeightObservers.delete(item)
			}
		}
	}

	const { state, setState } = useState<State>({
		gridStacks: [],
		active: false,
		initialized: false,
		blockBuilder: null,
	})

	const init = (props: InitProps): void => {
		const gridStackSelector = getGridStackTargetSelector()
		if (!gridStackSelector) {
			return
		}

		if (props.onInit) {
			props.onInit()
		}

		const localOptions = options

		if (window.innerWidth < 1000) {
			localOptions.column = 1
			localOptions.float = false
		}

		handleFloat(localOptions)

		Grid.setupDragIn(`.grid-stack-widgets ${selector.item}`, {
			helper: (event: Partial<DragEvent>) => {
				const target = event.target as GridItemHTMLElement
				const clone = Utils.cloneNode(target)

				const { blockBuilder } = state
				if (!blockBuilder) {
					return clone
				}

				cn.removeClass(blockBuilder, modifier.active)

				return clone
			},
		})

		const gridStacks = Grid.initAll(localOptions)
		setState({ gridStacks, ...props, initialized: true, blockBuilder: blockBuilderSelector.getBlockBuilder() })
		onAddHandler(gridStacks)
		onResizeStartHandler(gridStacks)
		onResizeStopHandler(gridStacks)
		onDragStopHandler(gridStacks)
		handleCompact()

		if (state.active || state.enableResizing) {
			enableResizing()
		}

		if (props.onAfterInit) {
			props.onAfterInit()
		}
		if (props.onWindowResize) {
			windowResize(props.onWindowResize)
		}
	}

	const resizeToContent = (grids: Grid[] = []) => {
		if (grids.length > 0) {
			setState({ gridStacks: grids })
		}
		cleanupAutoHeightObservers()
		const { gridStacks } = state
		for (const grid of gridStacks) {
			for (const item of grid.getGridItems()) {
				if (Number(item.getAttribute(constant.selector.autoHeight)) === 1) {
					const chartContainer = getChartSelectors.graphicsContainer(item)
					if (chartContainer) {
						chartContainer.style.overflow = 'visible'
					}
					const content =
						resizerSelectors.getItemBlockContent(item) ||
						item.querySelector<HTMLElement>('.grid-stack-item-content') ||
						item
					const cellHeight =
						typeof grid.getCellHeight === 'function' ? grid.getCellHeight(true) : Number(options.cellHeight || 1)
					const safeCellHeight = cellHeight && cellHeight > 0 ? cellHeight : 1
					const childrenHeight = Array.from(content.children ?? []).reduce((acc, el) => {
						const child = el as HTMLElement
						const rect = child.getBoundingClientRect().height
						return acc + (rect || child.offsetHeight)
					}, 0)
					const rectHeight = content.getBoundingClientRect().height
					const contentHeight = Math.max(childrenHeight, rectHeight, content.scrollHeight, content.offsetHeight)
					const currentRows =
						item.gridstackNode?.h ?? Math.max(1, Math.ceil(item.getBoundingClientRect().height / safeCellHeight))
					if (contentHeight <= 0) {
						if (!autoHeightObservers.has(item)) {
							const observer = new ResizeObserver(() => {
								if (!document.body.contains(item)) {
									observer.disconnect()
									autoHeightObservers.delete(item)
									return
								}
								resizeToContent([grid])
							})
							observer.observe(content)
							autoHeightObservers.set(item, observer)
						}
						continue
					}
					const minH = Math.max(1, currentRows, Math.ceil(contentHeight / safeCellHeight))
					grid.removeWidget(item, false)
					grid.addWidget(item, { sizeToContent: true, minH: minH })
					if (!autoHeightObservers.has(item)) {
						const observer = new ResizeObserver(() => {
							if (!document.body.contains(item)) {
								observer.disconnect()
								autoHeightObservers.delete(item)
								return
							}
							resizeToContent([grid])
						})
						observer.observe(content)
						autoHeightObservers.set(item, observer)
					}
				}
			}
		}
	}

	const reinit = () => {
		const { gridStacks } = state
		if (gridStacks.length === 0) {
			return
		}

		init(state)
	}

	const destroy = () => {
		const { gridStacks } = state
		applyCallback(gridStacks, async (gridStack: Grid) => {
			gridStack.removeAll(false)
		})
		for (const observer of autoHeightObservers.values()) {
			observer.disconnect()
		}
		autoHeightObservers.clear()

		setState({ gridStacks: [], initialized: false })
	}

	const applyCallback = (gridStacks: Grid[], callback: (gridStack: Grid, index: number) => void) => {
		const { length } = gridStacks
		for (let i = 0; i < length; i++) {
			const gridStack = gridStacks[i]
			if (!gridStack) {
				continue
			}

			callback(gridStack, i)
		}
	}

	const resizeItem = (el: GridItemHTMLElement) => {
		const { gridStacks } = state
		if (gridStacks.length === 0) {
			return
		}

		const itemId = el.getAttribute(selector.id)
		applyCallback(gridStacks, (gridStack: Grid) => {
			const items = gridStack.getGridItems()
			const { length } = items
			if (length === 0) {
				return
			}

			for (let i = 0; i < length; i++) {
				const item = items[i]
				if (!item) {
					continue
				}

				const id = item.getAttribute(selector.id)
				if (id === itemId) {
					gridStack.resizeToContent(item)
				}
			}
		})
	}

	const onDragStopHandler = (gridStacks: Grid[]) => {
		const { onDragComplete } = state
		applyCallback(gridStacks, (gridStack: Grid) => {
			gridStack.on('dragstop', (event: Event, el: GridItemHTMLElement) => {
				AbstractResizer.makeLoading()
				if (onDragComplete) {
					onDragComplete(event, el)
				}
				save()
			})
		})
	}

	const onResizeStartHandler = (gridStacks: Grid[]) => {
		const { onResizeStart } = state
		applyCallback(gridStacks, (gridStack: Grid) => {
			gridStack.on('resizestart', (event: Event, el: GridItemHTMLElement) => {
				if (el.getAttribute(selector.autoHeight)) {
					el.removeAttribute(selector.autoHeight)
				}

				if (onResizeStart) {
					onResizeStart(event, el)
				}
			})
		})
	}

	const onResizeStopHandler = (gridStacks: Grid[]) => {
		const { onResizeComplete } = state
		applyCallback(gridStacks, (gridStack: Grid) => {
			gridStack.on('resizestop', (event: Event, el: GridItemHTMLElement) => {
				AbstractResizer.makeLoading()
				if (onResizeComplete) {
					onResizeComplete(event, el)
				}
				save()
			})
		})
	}

	const onAddHandler = (gridStacks: Grid[]) => {
		applyCallback(gridStacks, (gridStack: Grid) => {
			gridStack.on('added', (e: GridStackCustomEvent) => {
				addBlock(e, getSerializedData())
			})
		})
	}

	const windowResize = (callback: () => void) => {
		const resize = useEvent(window, 'resize')
		resize.register(
			debounce(() => {
				callback()
			}),
		)
	}

	const disableResizing = () => {
		const { gridStacks } = state
		if (gridStacks.length === 0) {
			return
		}

		applyCallback(gridStacks, (gridStack: Grid) => gridStack.disable())
		setState({ active: false, enableResizing: false })
	}

	const enableResizing = () => {
		const { gridStacks } = state
		if (gridStacks.length === 0) {
			return
		}

		applyCallback(gridStacks, (gridStack: Grid) => gridStack.enable())
		setState({ active: true, enableResizing: true })
	}

	const save = () => {
		$.nette.ajax({
			type: 'POST',
			url: window.resizeBlocks,
			data: {
				blockPositionSettings: getSerializedData(),
			},
		})

		setState({ active: false })
	}

	const getSerializedData = (): Array<GridStackOptions | GridStackWidget[]> => {
		const { gridStacks } = state
		const { length } = gridStacks
		if (length === 0) {
			return []
		}

		for (let i = 0; i < length; i++) {
			const gridStack = gridStacks[i]
			if (!gridStack) {
				continue
			}

			return [
				gridStack.save(false, undefined, (node: GridStackNode, w: GridStackWidget) => {
					const { el } = node
					if (!el) {
						return
					}

					if (el.getAttribute('gs-w') === null) {
						w.w = 1
					}

					const hasAutoHeight = Number(el.getAttribute(selector.autoHeight)) === 1
					if (hasAutoHeight) {
						w.h = 0
					}
				}),
			]
		}

		return []
	}

	const addBlock = (
		e: GridStackCustomEvent,
		blockPositionSettings?: Array<GridStackOptions | GridStackWidget[]> | undefined,
	) => {
		const target = e.target as HTMLElement
		if (!target) {
			return
		}

		const { children } = target
		if (!children) {
			return
		}

		const { length } = children
		if (length === 0) {
			return
		}

		const newOrder = []
		for (let i = 0; i < length; i++) {
			const child = children[i] as any

			if (!child) {
				continue
			}
			newOrder.push(child.dataset.blockEditor)
		}

		const newBlock = getGridStackNewBlockSelector(target)
		if (!newBlock) {
			return
		}

		const { dataset } = target
		const { templateName } = newBlock.dataset

		let wrapperId = '0'
		if (dataset && dataset.contextMenuId) {
			wrapperId = dataset.contextMenuId
		}

		$.nette.ajax({
			type: 'POST',
			url: window.addNewBlockLink,
			data: {
				templateName,
				wrapperId,
				newOrder,
				blockPositionSettings,
			},
		})

		newBlock.classList.remove('js-grid-stack-new-block')

		setState({ active: true })
	}

	const getInstances = (): Grid[] => state.gridStacks

	const changeGridsFloat = (float: boolean) => {
		const { gridStacks } = state
		if (gridStacks.length === 0) {
			return
		}

		applyCallback(gridStacks, (gridStack: Grid) => {
			gridStack.float(float)
		})
	}

	const handleFloat = (gridStackOptions: GridStackOptions) => {
		const floatHandler = getGridStackFloatHandler()
		if (!floatHandler) {
			return
		}

		let float: boolean
		if (localStorage.getItem(constant.localStorage.float) === null) {
			gridStackOptions.float = true
			localStorage.setItem(constant.localStorage.float, 'true')
			addClass(floatHandler, modifier.active)
		} else {
			float = localStorage.getItem('grid-stack-float') === 'true'
			gridStackOptions.float = float
			if (float) {
				addClass(floatHandler, modifier.active)
			}
		}

		useEvent(floatHandler, 'click').register(() => {
			float = localStorage.getItem(constant.localStorage.float) === 'true'

			if (float) {
				removeClass(floatHandler, modifier.active)
			} else {
				addClass(floatHandler, modifier.active)
			}

			float = !float
			changeGridsFloat(float)
			localStorage.setItem(constant.localStorage.float, String(float))
		})
	}

	const changeGridsCompact = () => {
		const { gridStacks } = state
		if (gridStacks.length === 0) {
			return
		}

		applyCallback(gridStacks, (gridStack: Grid) => {
			gridStack.compact()
		})
	}

	const handleCompact = () => {
		const compactSelector = getGridStackCompact()
		if (compactSelector) {
			changeGridsCompact()
		}

		const compactHandler = getGridStackCompactHandler()
		if (!compactHandler) {
			return
		}

		let compact: boolean
		if (localStorage.getItem(constant.localStorage.compact) === null) {
			localStorage.setItem(constant.localStorage.compact, 'false')
			removeClass(compactHandler, modifier.active)
		} else {
			compact = localStorage.getItem(constant.localStorage.compact) === 'true'
			if (compact) {
				addClass(compactHandler, modifier.active)
				changeGridsCompact()
			}
		}

		useEvent(compactHandler, 'click').register(() => {
			compact = localStorage.getItem(constant.localStorage.compact) === 'false'

			if (compact) {
				addClass(compactHandler, modifier.active)
			} else {
				removeClass(compactHandler, modifier.active)
			}

			changeGridsCompact()
			localStorage.setItem(constant.localStorage.compact, String(compact))
		})
	}

	return {
		init,
		resizeItem,
		reinit,
		destroy,
		disableResizing,
		enableResizing,
		save,
		getSerializedData,
		getInstances,
		resizeToContent,
		handleCompact,
	}
})()
